import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Currency Context
const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Currency Provider
export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('EUR');
  const [currencies, setCurrencies] = useState({});
  const [exchangeRates, setExchangeRates] = useState({});

  useEffect(() => {
    // Load saved currency from localStorage
    const savedCurrency = localStorage.getItem('preferred_currency');
    if (savedCurrency) {
      setCurrency(savedCurrency);
    }
    
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currency/supported`);
      setCurrencies(response.data);
      
      // Extract exchange rates
      const rates = {};
      Object.keys(response.data).forEach(code => {
        rates[code] = response.data[code].rate_from_eur;
      });
      setExchangeRates(rates);
    } catch (error) {
      console.error('Failed to fetch currencies:', error);
      // Fallback currencies
      const fallback = {
        'EUR': { symbol: '€', name: 'Euro', rate_from_eur: 1.0 },
      };
      setCurrencies(fallback);
      
      const fallbackRates = {};
      Object.keys(fallback).forEach(code => {
        fallbackRates[code] = fallback[code].rate_from_eur;
      });
      setExchangeRates(fallbackRates);
    }
  };

  const changeCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem('preferred_currency', newCurrency);
  };

  const convertPrice = (amount, fromCurrency = 'EUR', toCurrency = null) => {
    const target = toCurrency || currency;
    
    if (!exchangeRates[fromCurrency] || !exchangeRates[target]) {
      return amount;
    }

    // Convert to EUR first, then to target currency
    let eurAmount = amount;
    if (fromCurrency !== 'EUR') {
      eurAmount = amount / exchangeRates[fromCurrency];
    }
    
    if (target !== 'EUR') {
      return eurAmount * exchangeRates[target];
    }
    
    return eurAmount;
  };

  const formatPrice = (amount, currencyCode = null) => {
    const targetCurrency = currencyCode || currency;
    const currencyInfo = currencies[targetCurrency];
    
    if (!currencyInfo) {
      return `${amount?.toFixed(2)} ${targetCurrency}`;
    }

    const symbol = currencyInfo.symbol;
    const formatted = amount?.toFixed(2);
    
    // EUR only - standard formatting
    return `${symbol}${formatted}`;
  };

  const value = {
    currency,
    currencies,
    exchangeRates,
    changeCurrency,
    convertPrice,
    formatPrice
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Currency Selector Component  
export const CurrencySelector = ({ className = "" }) => {
  const { currency, currencies, changeCurrency } = useCurrency();
  const currentCurrency = currencies[currency];

  return (
    <Select value={currency || 'EUR'} onValueChange={changeCurrency}>
      <SelectTrigger className={`w-20 ${className}`} data-testid="currency-selector">
        <SelectValue>
          <span className="text-sm font-medium">
            {currentCurrency?.symbol || currency || 'EUR'}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {Object.entries(currencies).map(([code, info]) => (
          <SelectItem key={code} value={code || 'EUR'}>
            <div className="flex items-center gap-2">
              <span className="font-medium">{info.symbol}</span>
              <span className="text-xs text-gray-500">{code}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// Price Display Component
export const PriceDisplay = ({ 
  amount, 
  currency: sourceCurrency = 'EUR', 
  className = "",
  showOriginal = false 
}) => {
  const { convertPrice, formatPrice, currency: userCurrency } = useCurrency();

  const convertedAmount = convertPrice(amount, sourceCurrency, userCurrency);
  const formattedPrice = formatPrice(convertedAmount, userCurrency);
  
  const originalFormatted = formatPrice(amount, sourceCurrency);

  return (
    <div className={`${className}`}>
      <span className="font-semibold text-lg" data-testid="converted-price">
        {formattedPrice}
      </span>
      {showOriginal && sourceCurrency !== userCurrency && (
        <div className="text-xs text-gray-500" data-testid="original-price">
          Originally: {originalFormatted}
        </div>
      )}
    </div>
  );
};

// Currency conversion info component
export const CurrencyInfo = () => {
  const { currency, currencies, exchangeRates } = useCurrency();
  const currencyInfo = currencies[currency];

  if (!currencyInfo) return null;

  return (
    <Badge variant="outline" className="text-xs">
      1 EUR = {exchangeRates[currency]?.toFixed(2)} {currencyInfo.symbol}
    </Badge>
  );
};